import { useCallback, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { toast } from 'sonner';
import type { EstimateSubmitGateResult } from '@/lib/estimates/submit-gate';
import type { CreateEstimateDto, CustomerApprovalMethod } from '@/types/estimate';
import {
  finalizeEstimateSave,
  getEstimateEntityId,
} from '@/routes/_dashboard/dashboard/estimates/_shared';

type EstimateCustomFieldValue = { fieldDefinitionId: string; value: unknown };
type SendChannel = 'email' | 'sms';
// Which submit button kicked off the in-flight flow, so only that button spins.
type SubmitAction = 'save' | 'send';

interface CreateEstimateMutationLike {
  isPending: boolean;
  mutateAsync: (payload: CreateEstimateDto) => Promise<unknown>;
}

interface UpdateEstimateMutationLike {
  isPending: boolean;
  mutate: (
    payload: unknown,
    options: { onSuccess: (data: unknown) => void; onError?: (error: unknown) => void },
  ) => void;
}

interface SendEstimateMutationLike {
  isPending: boolean;
  mutate: (
    payload: {
      id: string;
      channels: SendChannel[];
      customerApprovalMethod?: CustomerApprovalMethod | null;
    },
    options: { onSuccess: () => void; onError: () => void },
  ) => void;
}

interface UpsertCustomFieldValuesLike {
  mutateAsync: (args: {
    entityType: 'estimate';
    entityId: string;
    values: EstimateCustomFieldValue[];
  }) => Promise<unknown>;
}

interface UseEstimateSubmitFlowArgs {
  /** The single submit decision, shared with the buttons that drive this flow. */
  submitGate: EstimateSubmitGateResult;
  isEditMode: boolean;
  editId?: string;
  /**
   * The estimate being edited is APPROVED: saving dismisses the approval, so
   * the save stops for a confirmation and then carries `dismissApproval`.
   */
  requiresApprovalDismissal?: boolean;
  duplicateId?: string;
  templateId?: string;
  payload: CreateEstimateDto;
  customerApprovalMethod: CustomerApprovalMethod | '';
  requireSignature: boolean;
  setRequireSignature: Dispatch<SetStateAction<boolean>>;
  onAddressValidationFailed: () => void;
  customFieldValues: EstimateCustomFieldValue[];
  excludedTemplateAttachmentIds: string[];
  excludedDuplicateAttachmentIds: string[];
  createEstimate: CreateEstimateMutationLike;
  updateEstimate: UpdateEstimateMutationLike;
  sendEstimate: SendEstimateMutationLike;
  upsertValues: UpsertCustomFieldValuesLike;
  invalidateEstimateCustomFieldValues: (estimateId: string) => void;
  navigateToEstimateDetail: (estimateId: string) => void;
  navigateToEstimateEdit: (estimateId: string) => void;
  onAfterCreate?: (estimateId: string) => Promise<void>;
}

export function useEstimateSubmitFlow({
  submitGate,
  isEditMode,
  editId,
  requiresApprovalDismissal = false,
  duplicateId,
  templateId,
  payload,
  customerApprovalMethod,
  requireSignature,
  setRequireSignature,
  onAddressValidationFailed,
  customFieldValues,
  excludedTemplateAttachmentIds,
  excludedDuplicateAttachmentIds,
  createEstimate,
  updateEstimate,
  sendEstimate,
  upsertValues,
  invalidateEstimateCustomFieldValues,
  navigateToEstimateDetail,
  navigateToEstimateEdit,
  onAfterCreate,
}: UseEstimateSubmitFlowArgs) {
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [pendingSendId, setPendingSendId] = useState<string | null>(null);
  const [showDismissApprovalConfirm, setShowDismissApprovalConfirm] = useState(false);
  // Every create path keeps running after `createEstimate.mutateAsync()` resolves
  // (onAfterCreate + finalizeEstimateSave copy attachments/custom fields, then we
  // navigate). The mutation's `isPending` only covers the request, so it flips
  // back mid-flow and re-enables every submit button → duplicate estimates. This
  // flag spans the WHOLE flow; `submitting` folds it in so both create actions
  // stay locked until navigation.
  const [flowPending, setFlowPending] = useState(false);
  // The button that owns the in-flight flow. Drives which button shows a
  // spinner — `submitting` only says *something* is running, not which action,
  // so without this both submit buttons spin at once. Cleared on error and when
  // a flow hands off to the send modal (the create is done, so nothing should
  // keep spinning while the user picks channels).
  const [activeAction, setActiveAction] = useState<SubmitAction | null>(null);
  const flowRef = useRef(false);
  // What `signatureRequired` the created draft actually carries. A ref, not
  // state: nothing renders from it, and it must be readable by the send
  // confirm without re-running the callback that captured it.
  const createdSignatureRequiredRef = useRef(false);

  // Runs an async create flow exactly once. A synchronous ref guard rejects
  // re-entrant clicks before the state-driven disable can apply.
  //
  // On SUCCESS we deliberately stay locked: the flow ends by triggering a
  // navigation (fire-and-forget `router.navigate`), so releasing here would
  // re-enable the buttons before the redirect actually settles, leaving a window
  // to create a duplicate on a slow transition. The lock is meant to hold until
  // this form unmounts. We only release on ERROR, so a failed attempt can retry.
  const runCreateFlow = useCallback(async (action: SubmitAction, flow: () => Promise<void>) => {
    if (flowRef.current) return;
    flowRef.current = true;
    setActiveAction(action);
    setFlowPending(true);
    try {
      await flow();
    } catch (error) {
      flowRef.current = false;
      setActiveAction(null);
      setFlowPending(false);
      throw error;
    }
  }, []);

  const submitting = useMemo(
    () =>
      flowPending || createEstimate.isPending || updateEstimate.isPending || sendEstimate.isPending,
    [flowPending, createEstimate.isPending, updateEstimate.isPending, sendEstimate.isPending],
  );

  // Per-button spinner state. Only the clicked button spins; the others stay
  // disabled-but-idle while a flow runs.
  const savePending = activeAction === 'save';
  const sendPending = activeAction === 'send';

  // Every write entry point below runs this. The rules themselves live in
  // `evaluateEstimateSubmitGate`, so the
  // buttons' disabled state and this click-time check can never disagree.
  const ensureSubmittable = useCallback(() => {
    if (submitGate.ok) return true;
    if (submitGate.reveal === 'addressErrors') onAddressValidationFailed();
    toast.error(submitGate.message);
    return false;
  }, [submitGate, onAddressValidationFailed]);

  // The estimate row already exists before its custom fields are persisted.
  // A post-create failure must therefore never bubble back into runCreateFlow's
  // "safe to retry create" branch: another click would create a duplicate.
  // Keep the flow locked and hand the user off to the created estimate instead.
  const finalizeCreatedEstimate = useCallback(
    async (args: Parameters<typeof finalizeEstimateSave>[0]): Promise<boolean> => {
      try {
        await finalizeEstimateSave(args);
        return true;
      } catch {
        navigateToEstimateEdit(args.estimateId);
        return false;
      }
    },
    [navigateToEstimateEdit],
  );

  // The edit-mode save. `dismissApproval` is only ever true from the
  // approval-dismissal confirm below: an APPROVED estimate's PATCH is refused
  // (409) without it, and it must never ride along on an ordinary save.
  const runUpdate = useCallback(
    (dismissApproval: boolean) => {
      if (!editId) return;
      setActiveAction('save');
      updateEstimate.mutate(
        { id: editId, ...payload, ...(dismissApproval ? { dismissApproval: true } : {}) },
        {
          onSuccess: async (data: unknown) => {
            const id = getEstimateEntityId(data, editId);
            await finalizeEstimateSave({
              estimateId: id,
              isEditMode,
              customFieldValues,
              onInvalidateEditValues: invalidateEstimateCustomFieldValues,
              onCreateValues: (args) => upsertValues.mutateAsync(args),
              copyAttachments: false,
            });
            navigateToEstimateDetail(id);
          },
          // Only release the spinner here. Do NOT toast: useUpdateEstimate goes
          // through useApiMutation, whose `showErrorToast` defaults to true, so
          // it has already toasted by the time this runs — React Query fires the
          // hook-level onError AND this mutate-level one. Its toast is also the
          // better of the two, because it turns a 400's per-field validation
          // errors into "Fix the <field> field" instead of a raw message.
          onError: () => setActiveAction(null),
        },
      );
    },
    [
      editId,
      updateEstimate,
      payload,
      isEditMode,
      customFieldValues,
      invalidateEstimateCustomFieldValues,
      upsertValues,
      navigateToEstimateDetail,
    ],
  );

  // Saving an APPROVED estimate dismisses its approval, so the click stops
  // here for a confirmation and `handleDismissApprovalConfirm` finishes it.
  const handleDismissApprovalConfirm = useCallback(() => {
    setShowDismissApprovalConfirm(false);
    runUpdate(true);
  }, [runUpdate]);
  const handleDismissApprovalCancel = useCallback(() => {
    setShowDismissApprovalConfirm(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!ensureSubmittable()) return;

    if (isEditMode && editId) {
      if (requiresApprovalDismissal) {
        setShowDismissApprovalConfirm(true);
        return;
      }
      runUpdate(false);
      return;
    }

    await runCreateFlow('save', async () => {
      const data = await createEstimate.mutateAsync(payload);
      const id = getEstimateEntityId(data);
      await onAfterCreate?.(id);
      const finalized = await finalizeCreatedEstimate({
        estimateId: id,
        isEditMode,
        customFieldValues,
        onInvalidateEditValues: invalidateEstimateCustomFieldValues,
        onCreateValues: (args) => upsertValues.mutateAsync(args),
        templateId,
        excludedTemplateAttachmentIds,
        duplicateId,
        excludedDuplicateAttachmentIds,
        onCopyAttachmentsError: (error) =>
          console.error('[estimate attachments copy] failed:', error),
      });
      if (!finalized) return;
      navigateToEstimateDetail(id);
    });
  }, [
    ensureSubmittable,
    isEditMode,
    editId,
    requiresApprovalDismissal,
    runUpdate,
    payload,
    customFieldValues,
    invalidateEstimateCustomFieldValues,
    upsertValues,
    navigateToEstimateDetail,
    createEstimate,
    templateId,
    excludedTemplateAttachmentIds,
    duplicateId,
    excludedDuplicateAttachmentIds,
    onAfterCreate,
    runCreateFlow,
    finalizeCreatedEstimate,
  ]);

  const handleSubmitAndSend = useCallback(async () => {
    if (!ensureSubmittable()) return;

    const signatureRequired = customerApprovalMethod
      ? customerApprovalMethod === 'SIGNATURE'
      : requireSignature;
    setRequireSignature(signatureRequired);
    // What the row will actually carry once created. `handleSendConfirm` has
    // to diff the sender's final answer against THIS, not against a default,
    // because the modal opens after the create has already committed it.
    createdSignatureRequiredRef.current = signatureRequired;
    await runCreateFlow('send', async () => {
      const data = await createEstimate.mutateAsync({
        ...payload,
        signatureRequired,
      });
      const newId = getEstimateEntityId(data);
      await onAfterCreate?.(newId);
      const finalized = await finalizeCreatedEstimate({
        estimateId: newId,
        isEditMode: false,
        customFieldValues,
        onInvalidateEditValues: invalidateEstimateCustomFieldValues,
        onCreateValues: (args) => upsertValues.mutateAsync(args),
        templateId,
        excludedTemplateAttachmentIds,
        copyAttachments: true,
        onCopyAttachmentsError: (error) =>
          console.error('[template-attachments] copy failed:', error),
      });
      if (!finalized) return;
      setPendingSendId(newId);
      setShowSendConfirm(true);
      // The draft is saved; now we're just waiting on the user's channel
      // choice in the modal, so stop spinning the Save & Send button. The
      // buttons stay disabled (flowPending holds the lock) but no longer spin.
      setActiveAction(null);
    });
  }, [
    ensureSubmittable,
    createEstimate,
    payload,
    customerApprovalMethod,
    requireSignature,
    setRequireSignature,
    customFieldValues,
    invalidateEstimateCustomFieldValues,
    upsertValues,
    templateId,
    excludedTemplateAttachmentIds,
    onAfterCreate,
    runCreateFlow,
    finalizeCreatedEstimate,
  ]);

  const handleSendConfirm = useCallback(
    (channels: SendChannel[]) => {
      if (!pendingSendId) return;

      const id = pendingSendId;
      setShowSendConfirm(false);
      setPendingSendId(null);
      // The modal is gone but the real update/send is now in flight, and the
      // form stays mounted (disabled) until navigation. Resume the Save & Send
      // spinner so a slow email/SMS send isn't a dead, spinner-less page.
      setActiveAction('send');

      const doSend = () =>
        sendEstimate.mutate(
          {
            id,
            channels,
            // NOT-584 (A4) — the estimate was already saved before this modal
            // opened, so a method changed HERE never rode the create payload.
            // It has to travel with the send or the pick is silently lost.
            //
            // That includes "Not specified": the create payload carried the
            // tenant's Settings default, so omitting the key here would leave
            // that default stored and sent no matter what the sender chose.
            // Null is how the send says "clear it". Unlike the detail-page
            // resend there is no "untouched" state to preserve — this form
            // always holds an answer — so the field always travels.
            customerApprovalMethod: customerApprovalMethod || null,
          },
          {
            onSuccess: () => navigateToEstimateDetail(id),
            onError: () => navigateToEstimateDetail(id),
          },
        );

      // NOT-584 (A4) — write whenever the sender's answer differs from what
      // the draft was created with, in EITHER direction.
      //
      // This used to only ever write `false`, on the assumption that the
      // create payload had already settled the `true` case. It had not: the
      // create runs before the modal opens, so switching a non-signature
      // default to SIGNATURE in the modal left the row at
      // `signatureRequired: false` and sent it that way — a signature-approval
      // estimate that never collects one. Diffing against the created value
      // also drops the redundant PATCH when nothing changed.
      if (requireSignature !== createdSignatureRequiredRef.current) {
        updateEstimate.mutate(
          { id, signatureRequired: requireSignature },
          { onSuccess: doSend, onError: doSend },
        );
        return;
      }

      doSend();
    },
    [
      pendingSendId,
      customerApprovalMethod,
      requireSignature,
      sendEstimate,
      navigateToEstimateDetail,
      updateEstimate,
    ],
  );

  const handleSendCancel = useCallback(() => {
    setShowSendConfirm(false);
    if (pendingSendId) {
      navigateToEstimateEdit(pendingSendId);
    }
  }, [pendingSendId, navigateToEstimateEdit]);

  return {
    submitting,
    savePending,
    sendPending,
    showSendConfirm,
    showDismissApprovalConfirm,
    handleSubmit,
    handleSubmitAndSend,
    handleSendConfirm,
    handleSendCancel,
    handleDismissApprovalConfirm,
    handleDismissApprovalCancel,
  };
}
